import React, { useMemo } from 'react';
import { Autocomplete, Checkbox, TextField, Typography } from '@mui/material';

const optionLabel = (o) => o.label;
const sameOption = (o, v) => o.value === v.value;

// Multi-select with a search box (Brand, Category). Options are { value, label }; `value` is the
// array of selected values. Empty selection reads "All {plural}", like the other filters.
const SearchableMultiSelect = ({ id, label, allLabel, plural, options = [], value, onChange, sx, disabled = false }) => {
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  // Stable identity: Autocomplete clears the search text whenever `value` changes.
  const selected = useMemo(() => value.map((v) => byValue.get(v) || { value: v, label: v }), [value, byValue]);

  return (
    <Autocomplete
      id={id}
      multiple
      size="small"
      disableCloseOnSelect
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_e, next) => onChange(next.map((o) => o.value))}
      isOptionEqualToValue={sameOption}
      getOptionLabel={optionLabel}
      noOptionsText="No matches"
      ListboxProps={{ style: { maxHeight: 320 } }}
      sx={sx}
      renderTags={(v) => (
        <Typography component="span" variant="body2" noWrap sx={{ pl: 0.5, maxWidth: '100%' }}>
          {v.length === 1 ? v[0].label : `${v.length} ${plural}`}
        </Typography>
      )}
      renderOption={(props, option, { selected: isSelected }) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Checkbox size="small" checked={isSelected} sx={{ py: 0, mr: 1 }} />
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length ? 'Search' : allLabel}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        />
      )}
    />
  );
};

export default SearchableMultiSelect;
